import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Button,
  Box,
  Stack
} from '@mui/material';
import {
  Warning as WarningIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';

/**
 * LiabilityWatchList Component
 * Table of high-risk vendors requiring immediate attention
 * Filters: High data sensitivity (4-5), No DPA, Low trust score (<70%)
 */
export const LiabilityWatchList = ({ vendors }) => {
  const { t } = useTranslation(['dashboard']);
  const navigate = useNavigate();
  const [orderBy, setOrderBy] = useState('trustScore');
  const [order, setOrder] = useState('asc');

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedVendors = React.useMemo(() => {
    if (!vendors || !Array.isArray(vendors)) return [];
    return [...vendors].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (orderBy === 'criticalDeficiencies') {
        aValue = a.criticalDeficiencies?.length || 0;
        bValue = b.criticalDeficiencies?.length || 0;
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [vendors, orderBy, order]);

  const getTierBadge = (tier) => {
    const badges = {
      1: { label: 'Tier 1', color: 'error' },
      2: { label: 'Tier 2', color: 'warning' },
      3: { label: 'Tier 3', color: 'success' }
    };
    return badges[tier] || { label: 'Unknown', color: 'default' };
  };

  const getTrustScoreColor = (score) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="error" />
            <Typography variant="h6">{t('dashboard:liabilityWatch.title')}</Typography>
          </Box>
          <Chip
            label={t('dashboard:liabilityWatch.highRiskCount', { count: sortedVendors.length })}
            color="error"
            size="small"
          />
        </Box>

        <Typography variant="body2" color="textSecondary" mb={2}>
          {t('dashboard:liabilityWatch.subtitle')}
        </Typography>

        {sortedVendors.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="textSecondary">
              {t('dashboard:liabilityWatch.noRisk')}
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('dashboard:liabilityWatch.table.vendor')}</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'tier'}
                      direction={orderBy === 'tier' ? order : 'asc'}
                      onClick={() => handleSort('tier')}
                    >
                      {t('dashboard:liabilityWatch.table.tier')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'trustScore'}
                      direction={orderBy === 'trustScore' ? order : 'asc'}
                      onClick={() => handleSort('trustScore')}
                    >
                      {t('dashboard:liabilityWatch.table.trustScore')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>{t('dashboard:liabilityWatch.table.alerts')}</TableCell>
                  <TableCell align="right">{t('dashboard:liabilityWatch.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedVendors.map((vendor) => {
                  const tierBadge = getTierBadge(vendor.tier);
                  const alerts = [];

                  if (!vendor.dpaEstablished) alerts.push({ label: t('dashboard:liabilityWatch.alerts.noDpa'), color: 'error' });
                  if (vendor.trustScore < 70) alerts.push({ label: t('dashboard:liabilityWatch.alerts.lowScore'), color: 'error' });
                  if (vendor.recertificationDue) alerts.push({ label: t('dashboard:liabilityWatch.alerts.recertificationDue'), color: 'error' });
                  if ((vendor.criticalDeficiencies?.length || 0) > 3) {
                    alerts.push({ label: t('dashboard:liabilityWatch.alerts.gaps', { count: vendor.criticalDeficiencies.length }), color: 'warning' });
                  }
                  if (vendor.dataSensitivity >= 4) alerts.push({ label: t('dashboard:liabilityWatch.alerts.highSensitivity'), color: 'warning' });

                  return (
                    <TableRow key={vendor._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {vendor.vendorName || vendor.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={tierBadge.label}
                          color={tierBadge.color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${vendor.trustScore}%`}
                          color={getTrustScoreColor(vendor.trustScore)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {alerts.map((alert, idx) => (
                            <Chip
                              key={idx}
                              label={alert.label}
                              color={alert.color}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => navigate(`/vendor-assessments/${vendor._id}`)}
                        >
                          {t('dashboard:liabilityWatch.review')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};
